import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowUp, ArrowDown, Plus, Trash2, GripVertical, RefreshCw } from 'lucide-react';
import { api } from '../../lib/api';
import type { SiteSettings } from '../../lib/types';

interface NavItem { label: string; href: string }
interface NavData { es: NavItem[]; en: NavItem[] }

function parse<T>(json: string | null | undefined, fallback: T): T {
  try { return JSON.parse(json ?? '') as T; } catch { return fallback; }
}

export default function HeaderEditorPage() {
  const navigate = useNavigate();

  const [nav, setNav] = useState<NavData>({ es: [], en: [] });
  const [locale, setLocale] = useState<'es' | 'en'>('es');
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [settings, setSettings] = useState<SiteSettings | null>(null);
  const [previewSrc, setPreviewSrc] = useState<string | null>(null);
  const [noPages, setNoPages] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  const previewBaseRef = useRef<string | null>(null);
  const tokenAtRef = useRef(0);
  const homePageIdRef = useRef<string | null>(null);

  async function ensureToken(pageId: string) {
    if (previewBaseRef.current && Date.now() - tokenAtRef.current < 13 * 60 * 1000) return;
    const { url } = await api.preview.getUrl(pageId);
    previewBaseRef.current = url;
    tokenAtRef.current = Date.now();
  }

  async function loadPreview(pageId?: string) {
    const id = pageId ?? homePageIdRef.current;
    if (!id) return;
    try {
      await ensureToken(id);
      if (!previewBaseRef.current) return;
      setPreviewSrc(`${previewBaseRef.current}&v=${Date.now()}`);
    } catch {
      // preview best-effort
    }
  }

  useEffect(() => {
    api.settings.get().then((s) => {
      if (!s) return;
      setSettings(s);
      setNav(parse(s.navJson, { es: [], en: [] }));
    }).catch(console.error);

    api.pages.list().then(async (pages) => {
      if (pages.length === 0) { setNoPages(true); return; }
      const home = pages.find((p) => p.slug === 'home' || p.slug === '/') ?? pages[0];
      homePageIdRef.current = home.id;
      await loadPreview(home.id);
    }).catch(console.error);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const items = nav[locale];
  const selectedItem = selectedIndex !== null ? items[selectedIndex] ?? null : null;

  function updateNav(newNav: NavData) {
    setNav(newNav);
    setIsDirty(true);
  }

  function updateItem(index: number, patch: Partial<NavItem>) {
    updateNav({ ...nav, [locale]: items.map((it, i) => i === index ? { ...it, ...patch } : it) });
  }

  function addItem() {
    const newItems = [...items, { label: '', href: '' }];
    updateNav({ ...nav, [locale]: newItems });
    setSelectedIndex(newItems.length - 1);
  }

  function removeItem(index: number) {
    updateNav({ ...nav, [locale]: items.filter((_, i) => i !== index) });
    setSelectedIndex(null);
  }

  function moveItem(index: number, dir: -1 | 1) {
    const next = index + dir;
    if (next < 0 || next >= items.length) return;
    const newItems = [...items];
    [newItems[index], newItems[next]] = [newItems[next], newItems[index]];
    updateNav({ ...nav, [locale]: newItems });
    setSelectedIndex(next);
  }

  const save = useCallback(async () => {
    if (!settings) return;
    setSaving(true);
    try {
      await api.settings.update({ ...settings, navJson: JSON.stringify(nav) });
      setIsDirty(false);
      setSavedAt(new Date());
      await loadPreview();
    } finally {
      setSaving(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings, nav]);

  const saveLabel = saving ? 'Guardando…'
    : isDirty ? 'Cambios sin guardar'
    : savedAt ? `Guardado · ${savedAt.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}`
    : 'Sin cambios';

  return (
    <div className="flex flex-col h-screen bg-ui-bg text-ui-text overflow-hidden">

      {/* ── Top bar ─────────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-2 px-3 py-[10px] border-b border-ui-border bg-ui-surface shrink-0">
        <button onClick={() => navigate('/site?tab=paginas')}
          className="rounded-md border border-ui-border px-3 py-1.5 text-sm text-ui-text-muted hover:text-ui-text hover:border-ui-border-strong transition-colors">
          ← Páginas
        </button>
        <span className="text-sm font-semibold text-ui-text">Encabezado</span>
        <span className="text-xs text-ui-text-subtle ml-1">{saveLabel}</span>
        <div className="ml-auto">
          <button onClick={save} disabled={saving || !isDirty || !settings}
            className="px-4 py-1.5 bg-ui-accent text-ui-text-inverse text-sm font-medium rounded-md disabled:opacity-40 hover:opacity-90 transition-opacity">
            {saving ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </div>

      {/* ── Body ────────────────────────────────────────────────────────────── */}
      <div className="flex flex-1 overflow-hidden">

        {/* ── Left: Navigator ─────────────────────────────────────────────── */}
        <div className="w-[240px] shrink-0 flex flex-col border-r border-ui-border bg-ui-surface">
          <div className="p-4 border-b border-ui-border space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-ui-text">Menú de navegación</h2>
              <span className="text-xs text-ui-text-subtle">{items.length}</span>
            </div>
            <div className="flex gap-1">
              {(['es', 'en'] as const).map((l) => (
                <button key={l} onClick={() => { setLocale(l); setSelectedIndex(null); }}
                  className={`flex-1 py-1 text-xs font-semibold rounded transition-colors ${
                    locale === l ? 'bg-ui-accent text-ui-text-inverse' : 'text-ui-text-muted hover:text-ui-text'
                  }`}>
                  {l.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {items.map((item, i) => (
              <div key={i} role="button" tabIndex={0}
                onClick={() => setSelectedIndex(i)}
                onKeyDown={(e) => e.key === 'Enter' && setSelectedIndex(i)}
                className={`cursor-pointer rounded-xl border p-3 transition-colors ${
                  selectedIndex === i
                    ? 'border-ui-accent bg-ui-accent/10'
                    : 'border-ui-border hover:border-ui-border-strong'
                }`}>
                <div className="flex items-start gap-2">
                  <GripVertical size={13} className="text-ui-text-subtle mt-0.5 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-ui-text truncate">
                      {item.label || <span className="text-ui-text-subtle italic text-xs">Sin etiqueta</span>}
                    </p>
                    <p className="text-xs text-ui-text-subtle font-mono truncate mt-0.5">{item.href || '—'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1 mt-2 pl-[18px]">
                  <button onClick={(e) => { e.stopPropagation(); moveItem(i, -1); }} disabled={i === 0}
                    className="p-1 rounded text-ui-text-subtle hover:text-ui-text disabled:opacity-30 transition-colors">
                    <ArrowUp size={11} />
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); moveItem(i, 1); }} disabled={i === items.length - 1}
                    className="p-1 rounded text-ui-text-subtle hover:text-ui-text disabled:opacity-30 transition-colors">
                    <ArrowDown size={11} />
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); removeItem(i); }}
                    className="p-1 rounded text-ui-text-subtle hover:text-red-500 transition-colors ml-auto">
                    <Trash2 size={11} />
                  </button>
                </div>
              </div>
            ))}
            {items.length === 0 && (
              <p className="text-sm text-ui-text-subtle text-center py-6">Sin ítems en {locale.toUpperCase()}.</p>
            )}
          </div>

          <div className="p-3 border-t border-ui-border">
            <button onClick={addItem}
              className="w-full flex items-center justify-center gap-1.5 py-2 text-sm text-ui-accent hover:opacity-80 border border-dashed border-ui-border rounded-lg transition-opacity">
              <Plus size={13} /> Añadir ítem
            </button>
          </div>
        </div>

        {/* ── Center: Preview ──────────────────────────────────────────────── */}
        <div className="flex-1 flex flex-col overflow-hidden bg-[#f5f5f5]">
          {noPages ? (
            <div className="flex-1 flex flex-col items-center justify-center gap-3 text-center px-8">
              <p className="text-sm text-ui-text-subtle">
                Crea al menos una página para ver la vista previa del encabezado.
              </p>
            </div>
          ) : previewSrc ? (
            <iframe key={previewSrc} src={previewSrc} title="Vista previa"
              className="flex-1 w-full border-0 bg-white" />
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="w-5 h-5 border-2 border-ui-accent border-t-transparent rounded-full animate-spin" />
            </div>
          )}
          <div className="flex items-center justify-between px-4 py-2 bg-ui-surface border-t border-ui-border shrink-0">
            <span className="text-xs text-ui-text-subtle">
              {noPages ? 'Sin páginas creadas' : 'Guarda para ver los cambios reflejados'}
            </span>
            {previewSrc && (
              <button onClick={() => loadPreview()}
                className="flex items-center gap-1.5 text-xs text-ui-accent hover:opacity-80 transition-opacity">
                <RefreshCw size={11} /> Recargar
              </button>
            )}
          </div>
        </div>

        {/* ── Right: Inspector ─────────────────────────────────────────────── */}
        <div className="w-[280px] shrink-0 flex flex-col border-l border-ui-border bg-ui-surface">
          <div className="p-4 border-b border-ui-border">
            <h2 className="text-sm font-semibold text-ui-text">Inspector</h2>
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            {selectedItem ? (
              <div className="space-y-4">
                <p className="text-xs text-ui-text-subtle uppercase tracking-wider font-medium">
                  Ítem {selectedIndex! + 1} de {items.length}
                </p>
                <div>
                  <label className="block text-sm text-ui-text-muted mb-1">Etiqueta</label>
                  <input value={selectedItem.label}
                    onChange={(e) => updateItem(selectedIndex!, { label: e.target.value })}
                    placeholder="Inicio"
                    className="w-full bg-ui-bg border border-ui-border rounded px-3 py-2 text-sm text-ui-text focus:outline-none focus:border-ui-accent" />
                </div>
                <div>
                  <label className="block text-sm text-ui-text-muted mb-1">Ruta (href)</label>
                  <input value={selectedItem.href}
                    onChange={(e) => updateItem(selectedIndex!, { href: e.target.value })}
                    placeholder="/"
                    className="w-full bg-ui-bg border border-ui-border rounded px-3 py-2 text-sm text-ui-text font-mono focus:outline-none focus:border-ui-accent" />
                  <p className="text-xs text-ui-text-subtle mt-1">
                    Usa <span className="font-mono">/</span> para inicio, <span className="font-mono">/servicios</span> para una sección interna, o una URL completa para enlaces externos.
                  </p>
                </div>
                <div className="pt-2 border-t border-ui-border">
                  <button onClick={() => removeItem(selectedIndex!)}
                    className="flex items-center gap-1.5 text-sm text-ui-danger hover:opacity-80 transition-colors">
                    <Trash2 size={13} /> Eliminar ítem
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 space-y-2">
                <p className="text-sm font-medium text-ui-text-muted">Sin selección</p>
                <p className="text-xs text-ui-text-subtle">
                  Selecciona un ítem del menú para editar sus propiedades.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
